import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    TextField,
    InputAdornment,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

// ─── Parámetros maestros: dónde se configuran y a qué afectan ───
const PARAMETROS = [
    {
        parametro: 'Mín / Máx de Temperatura, Humedad, Amperaje y RPM',
        ruta: '/maquinas/parametros',
        pantalla: 'Parámetros de Máquinas',
        coleccion: 'ParametrosMaquina',
        afecta: 'Colores y parpadeo del Tablero principal; rango de los indicadores circulares; alarma de amperaje (se dispara cuando amperaje ≥ maxAmp).',
    },
    {
        parametro: 'RPM máximo por máquina',
        ruta: '/maquinas/valores-eficiencia',
        pantalla: 'Valores de Eficiencia',
        coleccion: 'ValoresEficiencia',
        afecta: 'Eficiencia guardada cada 10 min = (promedio RPM ÷ RPM máximo) × 100. Alimenta Tabla de Eficiencia y gráficas por día.',
    },
    {
        parametro: 'Revoluciones por metro (por referencia y máquina)',
        ruta: '/maquinas/referencias',
        pantalla: 'Referencias',
        coleccion: 'Referencia.maquinas[]',
        afecta: 'Conversión de vueltas a metros: metros = revoluciones ÷ rev/metro. Usado en Producción por Referencia, Acumulado Operarios, Donut metros por turno.',
    },
    {
        parametro: 'Rendimiento práctico (por referencia y máquina)',
        ruta: '/maquinas/referencias',
        pantalla: 'Referencias',
        coleccion: 'Referencia.maquinas[]',
        afecta: 'Se muestra como dato de referencia en Producción por Referencia y Programación IA (estimaciones).',
    },
    {
        parametro: 'Horarios de turnos (inicio / fin) por periodo',
        ruta: '/maquinas/formulario-guardar-turnos',
        pantalla: 'Guardar Turnos',
        coleccion: 'ParametrosTurnos',
        afecta: 'Ventanas horarias de la Tabla segmentos horas, asignación de operarios y cálculo de horas por operario.',
    },
    {
        parametro: 'Asignación máquina → referencia (rango de fechas)',
        ruta: '/maquinas/asignar-referencia',
        pantalla: 'Asignar Referencia',
        coleccion: 'AsignacionReferencia',
        afecta: 'Define qué rev/metro se usa para cada máquina cada día. Sin asignación, la producción en metros sale en 0.',
    },
    {
        parametro: 'Asignación operario → máquina / turno',
        ruta: '/maquinas/asignar-operarios',
        pantalla: 'Asignar Operarios',
        coleccion: 'AsignacionOperario',
        afecta: 'Acumulado por operario, horas por operario y metros por turno.',
    },
    {
        parametro: 'Tipo, subtipo, proceso y potencia',
        ruta: '/maquinas/parametros/tipos',
        pantalla: 'Clasificación de Máquinas',
        coleccion: 'TipoMaquina',
        afecta: 'Filtros de máquinas en Asignar Referencia y Características.',
    },
    {
        parametro: 'Reglas de programación, cargos, ciclo de rotación y ausencias',
        ruta: '/maquinas/parametros/reglas-programacion',
        pantalla: 'Reglas / Cargos / Agente / Ausencias',
        coleccion: 'ReglasProgramacion, Cargo, ConfiguracionAgente, AusenciaOperario',
        afecta: 'Generación automática de turnos del Agente de Turnos.',
    },
];

// ─── Valores fijos en código que conviene tener presentes al cuadrar ───
const VALORES_FIJOS = [
    {
        donde: 'src/services/mqttLogger.js',
        valor: 'maxRPM = 30',
        efecto: 'El promedio de RPM se recorta a 30 antes de calcular la eficiencia. Si el RPM máximo configurado es mayor a 30, la eficiencia nunca llegará al 100 %.',
    },
    {
        donde: 'frontend/src/components/datos.js',
        valor: 'eficiencia = rpm / 35 × 100',
        efecto: 'La eficiencia en vivo del Tablero principal usa 35 fijo y no el RPM máximo de Valores de Eficiencia.',
    },
    {
        donde: 'src/services/mqttLogger.js',
        valor: 'Intervalo de guardado = 10 min',
        efecto: 'Cada lectura guardada es el promedio de 10 minutos; las tablas por hora y por día se construyen sobre esos bloques.',
    },
];

// ─── Catálogo de pantallas ───
const CATALOGO = [
    // Tablero
    {
        categoria: 'Tablero',
        nombre: 'Tablero principal',
        ruta: '/datos',
        proposito: 'Monitoreo en tiempo real de todas las máquinas (socket.io).',
        muestra: 'Temperatura, humedad, amperaje, RPM, velocidad lineal y eficiencia instantánea por máquina.',
        fuente: 'Socket en vivo + GET /api/maquinas-parametros + GET /machine8/acumulado',
        calculo: 'Color de cada indicador: valor < mín → alerta baja; valor > máx → alerta alta (parpadea); en rango → color normal.',
        parametros: ['/maquinas/parametros'],
        cuadrar: 'Si un indicador parpadea sin razón, revisa los mín/máx de esa máquina en Parámetros de Máquinas.',
    },
    // Tablas
    {
        categoria: 'Tablas',
        nombre: 'Tabla de Eficiencia',
        ruta: '/tabla-promedio-rpm/eficiencia-table',
        proposito: 'Comparar la eficiencia diaria de cada máquina en un rango de fechas.',
        muestra: 'Matriz fecha × máquina con % de eficiencia, total por fecha y total por máquina. Exporta a PDF.',
        fuente: 'GET /api/maquinas-lecturas?fechaInicio&fechaFin',
        calculo: 'Eficiencia día = promedio de las lecturas de 10 min, donde cada lectura = (promedio RPM ÷ RPM máximo) × 100. Totales = promedio simple.',
        parametros: ['/maquinas/valores-eficiencia'],
        cuadrar: 'Cambiar el RPM máximo solo afecta lecturas nuevas; los días ya guardados conservan su eficiencia.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla componentes',
        ruta: '/MaquinasTable',
        proposito: 'Inventario de máquinas registradas y sus sensores.',
        muestra: 'Nombre, número de máquina, sensor y fecha de registro.',
        fuente: 'GET /maquinas',
        calculo: 'Sin cálculo; listado directo.',
        parametros: ['/maquinas/parametros/ingreso'],
        cuadrar: 'El número de máquina debe coincidir con el usado en referencias y asignaciones (ej. "Machine1").',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla Acumulado RPM',
        ruta: '/ultimo-acumulado-rpm',
        proposito: 'Ver las revoluciones acumuladas por máquina y día.',
        muestra: 'Matriz fecha × máquina con vueltas totales y totales.',
        fuente: 'GET /api/ultimo-acumulado-rpm?startDate&endDate',
        calculo: 'Suma del acumuladoRPM de las lecturas del día.',
        parametros: [],
        cuadrar: 'Es el dato bruto: si los metros no cuadran, compara contra esta tabla para saber si el problema es el sensor o la rev/metro.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla Acumulado Operarios',
        ruta: '/ultimo-acumulado-operarios',
        proposito: 'Producción atribuida a cada operario según sus turnos asignados.',
        muestra: 'Máquina, referencia, revoluciones y metros producidos por operario, con total.',
        fuente: 'GET /api/ultimo-acumulado-operarios',
        calculo: 'Revoluciones dentro de la ventana del turno ÷ rev/metro de la referencia activa en esa máquina ese día. Turnos de noche cruzan medianoche.',
        parametros: ['/maquinas/asignar-operarios', '/maquinas/asignar-referencia', '/maquinas/referencias'],
        cuadrar: 'Metros en 0 = falta asignación de referencia o rev/metro para esa máquina.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla Horas Operario',
        ruta: '/tablas/horas-operario',
        proposito: 'Horas trabajadas por un operario en un rango de fechas.',
        muestra: 'Fecha, horario del turno y total de horas.',
        fuente: 'GET /api/horas-operario?operarioId&fechaInicio&fechaFin',
        calculo: 'Duración de cada turno asignado (fin − inicio), sumada por día.',
        parametros: ['/maquinas/asignar-operarios', '/maquinas/formulario-guardar-turnos'],
        cuadrar: 'Si las horas no cuadran, revisa el horario del turno en Guardar Turnos y las ausencias del operario.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla segmentos horas',
        ruta: '/maquinas/parametros-turnos',
        proposito: 'Acumulado de producción dividido por turnos del día.',
        muestra: 'Filas turno / fecha × máquina con totales.',
        fuente: 'POST /api/acumulado-turnos-dia',
        calculo: 'Suma de vueltas de las lecturas que caen dentro de cada ventana de turno.',
        parametros: ['/maquinas/formulario-guardar-turnos'],
        cuadrar: 'Los segmentos salen de los horarios definidos para el periodo; si no hay periodo vigente, la tabla queda vacía.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Producción por Referencia',
        ruta: '/produccion-por-referencia',
        proposito: 'Metros producidos por referencia y máquina en un rango.',
        muestra: 'Fecha, máquina, referencia, revoluciones, rev/metro y producción (m), con subtotal.',
        fuente: 'GET /api/produccion-referencia?fechaInicio&fechaFin',
        calculo: 'Producción (m) = revoluciones del día ÷ revoluciones por metro.',
        parametros: ['/maquinas/referencias', '/maquinas/asignar-referencia'],
        cuadrar: 'Rev/metro muestra el valor usado; si es 0 la referencia no tiene configurada esa máquina.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Programa de Telas',
        ruta: '/programa-telas',
        proposito: 'Ver qué referencia está programada en cada máquina.',
        muestra: 'Asignaciones de referencia vigentes en el rango.',
        fuente: 'GET /asignar-referencia?fechaInicio&fechaFin',
        calculo: 'Sin cálculo; cruce de asignaciones por fechas.',
        parametros: ['/maquinas/asignar-referencia'],
        cuadrar: 'Fechas solapadas en una misma máquina generan referencias dobles en otras tablas.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla de Teñido',
        ruta: '/tablas/tabla-tenido',
        proposito: 'Seguimiento de las órdenes de teñido.',
        muestra: 'Fecha/hora, máquina, referencia, color, código, cantidad (kg) y comentarios.',
        fuente: 'GET /api/asignar-tenido',
        calculo: 'Sin cálculo; listado con notas editables.',
        parametros: ['/maquinas/asignar-tenido'],
        cuadrar: '—',
    },
    // Gráficas
    {
        categoria: 'Gráficas',
        nombre: 'Gráfica Máquina Días',
        ruta: '/graficas/produccion-maquina',
        proposito: 'Tendencia diaria de producción y eficiencia por máquina.',
        muestra: 'Series por día de vueltas y eficiencia promedio.',
        fuente: 'GET /api/maquinas-lista + producción por máquina',
        calculo: 'Promedio diario de eficiencia y suma diaria de vueltas.',
        parametros: ['/maquinas/valores-eficiencia'],
        cuadrar: '—',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Gráfica Máquina Hora',
        ruta: '/graficas/maquina-dia',
        proposito: 'Detalle hora a hora de una máquina en un día.',
        muestra: 'RPM, temperatura, humedad y amperaje por hora.',
        fuente: 'Lecturas de 10 min agrupadas por hora',
        calculo: 'Promedio horario de las lecturas.',
        parametros: ['/maquinas/parametros'],
        cuadrar: '—',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Amperajes por Máquina',
        ruta: '/graficas/amperajes-maquina',
        proposito: 'Comparar el consumo eléctrico diario.',
        muestra: 'Promedio de amperaje por día y máquina.',
        fuente: 'GET /promedio-amperaje-dia',
        calculo: 'Promedio de las lecturas de amperaje del día.',
        parametros: ['/maquinas/parametros'],
        cuadrar: 'Usa esta gráfica para fijar un maxAmp realista antes de activar alarmas.',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Amperaje Máquina Día',
        ruta: '/graficas/amperaje-maquina-dia',
        proposito: 'Amperaje hora a hora de una máquina.',
        muestra: 'Curva horaria de amperaje.',
        fuente: 'GET /amperaje-por-hora',
        calculo: 'Promedio horario.',
        parametros: ['/maquinas/parametros'],
        cuadrar: '—',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Acumulado Rama',
        ruta: '/graficas/acumulado-machine8',
        proposito: 'Metros lineales por día de la rama (Machine8).',
        muestra: 'Acumulado de metros lineales por día.',
        fuente: 'GET /api/machine8/acumulado-diario',
        calculo: 'Suma diaria de velocidad lineal.',
        parametros: [],
        cuadrar: '—',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Producción por Referencia (gráfica)',
        ruta: '/graficas/produccion-referencia',
        proposito: 'Visualizar los metros por referencia.',
        muestra: 'Barras de metros por referencia en el rango.',
        fuente: 'GET /produccion-referencia + /referencias',
        calculo: 'Igual que la tabla: revoluciones ÷ rev/metro.',
        parametros: ['/maquinas/referencias', '/maquinas/asignar-referencia'],
        cuadrar: '—',
    },
];

const NOMBRE_RUTA = Object.fromEntries([
    ...PARAMETROS.map(p => [p.ruta, p.pantalla]),
    ['/maquinas/parametros/ingreso', 'Ingreso de Máquina'],
    ['/maquinas/asignar-tenido', 'Asignar Teñido'],
]);

const CATEGORIAS = ['Todas', 'Tablero', 'Tablas', 'Gráficas'];

const Campo = ({ titulo, children }) => (
    <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#90caf9', textTransform: 'uppercase', letterSpacing: 1 }}>
            {titulo}
        </Typography>
        <Typography variant="body2" sx={{ color: '#e0e0e0' }}>{children}</Typography>
    </Box>
);

const CatalogoTablas = () => {
    const [busqueda, setBusqueda] = useState('');
    const [categoria, setCategoria] = useState('Todas');

    const filtrados = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return CATALOGO.filter(item =>
            (categoria === 'Todas' || item.categoria === categoria) &&
            (!q || Object.values(item).join(' ').toLowerCase().includes(q))
        );
    }, [busqueda, categoria]);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 300, mb: 1 }}>
                Catálogo de tablas
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Qué hace cada tabla y gráfica, de dónde sale el dato y qué parámetro hay que ajustar para cuadrarla.
            </Typography>

            {/* Mapa de parámetros */}
            <Typography variant="h6" sx={{ fontWeight: 400, mb: 1 }}>Mapa de parámetros</Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Parámetro</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Se configura en</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Qué afecta</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {PARAMETROS.map(p => (
                            <TableRow key={p.parametro}>
                                <TableCell sx={{ minWidth: 180 }}>{p.parametro}</TableCell>
                                <TableCell sx={{ minWidth: 160 }}>
                                    <Button component={Link} to={p.ruta} size="small" sx={{ textTransform: 'none', p: 0 }}>
                                        {p.pantalla}
                                    </Button>
                                    <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                        {p.coleccion}
                                    </Typography>
                                </TableCell>
                                <TableCell>{p.afecta}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Valores fijos */}
            <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Valores fijos en el código (no se cambian desde la página)</Typography>
                {VALORES_FIJOS.map(v => (
                    <Typography key={v.valor} variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{v.valor}</strong> — {v.efecto} <em style={{ opacity: 0.7 }}>({v.donde})</em>
                    </Typography>
                ))}
            </Alert>

            {/* Filtros */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Buscar tabla, parámetro o endpoint…"
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    sx={{ flex: '1 1 260px' }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
                {CATEGORIAS.map(c => (
                    <Chip
                        key={c}
                        label={c}
                        color={categoria === c ? 'primary' : 'default'}
                        variant={categoria === c ? 'filled' : 'outlined'}
                        onClick={() => setCategoria(c)}
                    />
                ))}
            </Box>

            {/* Catálogo */}
            {filtrados.length === 0 && (
                <Typography sx={{ color: 'text.secondary' }}>No hay resultados para la búsqueda.</Typography>
            )}
            {filtrados.map(item => (
                <Accordion key={item.ruta} disableGutters sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                            <Chip label={item.categoria} size="small" variant="outlined" />
                            <Typography sx={{ fontWeight: 500 }}>{item.nombre}</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>— {item.proposito}</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Campo titulo="Qué muestra">{item.muestra}</Campo>
                        <Campo titulo="Cálculo">{item.calculo}</Campo>
                        <Campo titulo="Fuente de datos"><code>{item.fuente}</code></Campo>
                        {item.cuadrar !== '—' && <Campo titulo="Para cuadrar">{item.cuadrar}</Campo>}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            <Button component={Link} to={item.ruta} variant="contained" size="small">
                                Abrir tabla
                            </Button>
                            {item.parametros.map(r => (
                                <Button key={r} component={Link} to={r} variant="outlined" size="small">
                                    {NOMBRE_RUTA[r] || r}
                                </Button>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Container>
    );
};

export default CatalogoTablas;
